import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/base/Sidebar/Sidebar';
import './ExamSubscription.css';

// LocalStorage key
const STORAGE_KEY = 'exam_subscription_data';

// Helper function to clean data of React event objects
const cleanData = (data) => {
  const clean = {};
  
  // List of expected data fields from your steps
  const expectedFields = [
    'selectedImage', 'correctImage', 'cpf', 'birthDate', 'fatherName',
    'schoolType', 'highSchoolStatus', 'language', 'accessibilityNeeds',
    'accessibilityResources', 'cep', 'addressData', 'addressNumber',
    'addressComplement', 'documentNumber', 'uf', 'issuingAgency',
    'birthUf', 'birthMunicipality', 'gender', 'race', 'maritalStatus',
    'nationality', 'nis', 'educationType', 'completionYear', 'encceja',
    'internetAccess', 'photo', 'examUf', 'examMunicipality', 'examType',
    'landline', 'mobile', 'email'
  ];
  
  // Only copy expected fields
  expectedFields.forEach(field => {
    if (data[field] !== undefined) {
      clean[field] = data[field];
    }
  });
  
  return clean;
};

// Helper function to transform flat data to backend expected structure
const transformToBackendFormat = (data, selectedExamInfo) => {
  // Extract address data from addressData object
  const addressData = data.addressData || {};
  
  // Combine birth location fields
  const birthLocation = data.birthUf && data.birthMunicipality 
    ? `${data.birthMunicipality}, ${data.birthUf}`
    : null;
  
  // Combine exam location fields
  const examLocation = data.examUf && data.examMunicipality
    ? `${data.examMunicipality}, ${data.examUf}`
    : null;
  
  // Use selected exam info if provided, otherwise use placeholder
  const selectedExam = selectedExamInfo ? {
    id: selectedExamInfo.id || 1,
    name: selectedExamInfo.name || 'ENEM 2026',
    selectedDate: selectedExamInfo.selectedDate || new Date().toISOString().split('T')[0]
  } : {
    id: 1,
    name: 'ENEM 2026',
    selectedDate: new Date().toISOString().split('T')[0]
  };
  
  // Transform photo info
  const photoInfo = data.photo ? {
    fileName: data.photo,
    fileSize: 0, // This would need to be extracted from the actual file
    fileType: 'image/jpeg' // This would need to be extracted from the actual file
  } : null;
  
  return {
    exam: selectedExam,
    personalInfo: {
      cpf: data.cpf,
      birthDate: data.birthDate,
      fatherName: data.fatherName,
      gender: data.gender,
      race: data.race,
      maritalStatus: data.maritalStatus,
      nationality: data.nationality,
      birthLocation: birthLocation
    },
    documentInfo: {
      documentNumber: data.documentNumber,
      uf: data.uf,
      issuingAgency: data.issuingAgency
    },
    educationInfo: {
      schoolType: data.schoolType,
      highSchoolStatus: data.highSchoolStatus,
      educationType: data.educationType,
      completionYear: data.completionYear,
      enccejaCertification: data.encceja,
      languageChoice: data.language
    },
    accessibilityInfo: {
      needs: data.accessibilityNeeds || [],
      resourcesNeeded: data.accessibilityResources
    },
    addressInfo: {
      cep: data.cep,
      street: addressData.logradouro || '',
      neighborhood: addressData.bairro || '',
      city: addressData.localidade || '',
      state: addressData.uf || '',
      number: data.addressNumber,
      complement: data.addressComplement
    },
    contactInfo: {
      landline: data.landline,
      mobile: data.mobile,
      email: data.email
    },
    examPreferences: {
      examType: data.examType,
      examLocation: examLocation
    },
    socioeconomicInfo: {
      nis: data.nis,
      internetAccess: data.internetAccess
    },
    photoInfo: photoInfo
  };
};

// Helper function to save to localStorage
const saveToLocalStorage = (data) => {
  try {
    // Clean the data before saving (remove React event objects)
    const cleanDataToSave = cleanData(data);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cleanDataToSave));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

// Helper function to load from localStorage
const loadFromLocalStorage = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    return null;
  }
};

// Helper function to clear localStorage
const clearLocalStorage = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
};

// --- Step Components ---

const StepIntroduction = ({ onNext, onResume, hasPreviousData }) => {
  return (
    <div className="esp-flow-step">
      <div className="esp-flow-chat-bubble">
        Mas, para se inscrever no Enem Digital é necessário estar atento às seguintes condições de participação:
        <ul>
          <li>É exclusivo para concluintes do Ensino Médio em 2020 ou para quem já concluiu o Ensino Médio em anos anteriores.</li>
          <li>Tem um número restrito de vagas por Estado e o Município, que serão preenchidas por quem se inscrever primeiro.</li>
          <li>Ele não permite alteração de opção para o Enem impresso após conclusão da inscrição.</li>
          <li>Não haverá recursos de acessibilidade, tais como: prova em braile, prova ledor tradutor intérprete de Língua Brasileira de Sinais (Libras), videoprova em Libras, prova com letra ampliada ou superampliada, uso de leitor de tela, guia-intérprete, auxílio para leitura, auxílio para transcrição, leitura labial, tempo adicional, sala de fácil acesso, mobiliário acessível. Os recursos de acessibilidade serão assegurados no Enem impresso.</li>
        </ul>
        O que você gostaria de fazer agora?
      </div>
      <div className="esp-flow-actions">
        {hasPreviousData && (
          <button onClick={() => onResume()} className="esp-btn-secondary">Resgatar preenchimento anterior</button>
        )}
        <button onClick={() => onNext()} className="esp-btn-primary">INSCRIÇÃO</button>
      </div>
    </div>
  );
};

const StepSecurityChallenge = ({ onNext, initialData = {} }) => {
  const [selectedImage, setSelectedImage] = useState(initialData.selectedImage || null);
  
  const allImages = [
    { name: 'Caminhão', emoji: '🚚' },
    { name: 'Balões', emoji: '🎈' },
    { name: 'Globo', emoji: '🌎' },
    { name: 'Tesoura', emoji: '✂️' },
    { name: 'Camiseta', emoji: '👕' }
  ];
  
  const [correctImage, setCorrectImage] = useState(() => {
    if (initialData.correctImage) {
      return allImages.find(img => img.name === initialData.correctImage) || allImages[0];
    }
    const randomIndex = Math.floor(Math.random() * allImages.length);
    return allImages[randomIndex];
  });

  const handleSelect = (imageName) => {
    setSelectedImage(imageName);
  };

  const handleSubmit = () => {
    if (!selectedImage) {
      alert('Por favor, selecione uma imagem antes de prosseguir.');
      return;
    }
    
    if (selectedImage === correctImage.name) { 
      onNext({ selectedImage, correctImage: correctImage.name });
    } else {
      alert(`Tente novamente. Você precisa selecionar a ${correctImage.name.toLowerCase()} para prosseguir.`);
    }
  };

  return (
    <div className="esp-flow-step">
      <div className="esp-flow-chat-bubble">
        Antes de prosseguirmos, preciso que você resolva um desafio.
        <br />
        Qual das figuras abaixo é uma imagem de <strong>"{correctImage.name}"</strong>?
      </div>
      <div className="esp-flow-challenge-images">
        {allImages.map((image) => (
          <div 
            key={image.name}
            className={`esp-challenge-image ${selectedImage === image.name ? 'esp-selected' : ''}`} 
            onClick={() => handleSelect(image.name)}
          >
            <div className="esp-image-placeholder">{image.emoji}</div>
            <div className="esp-image-label">{image.name}</div>
          </div>
        ))}
      </div>
      <div className="esp-flow-actions">
        <button onClick={handleSubmit} className="esp-btn-primary">PRÓXIMO →</button>
      </div>
    </div>
  );
};

const StepGetCPF = ({ onNext, initialData = {} }) => {
  const [cpf, setCpf] = useState('');

  const formatCPF = (value) => {
    const digits = value.replace(/\D/g, '').substring(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
  };

  useEffect(() => {
    if (initialData.cpf) {
      setCpf(formatCPF(initialData.cpf));
    }
  }, [initialData.cpf]);

  const handleChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').substring(0, 11);
    setCpf(formatCPF(digits));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const digits = cpf.replace(/\D/g, '');
    if (digits.length === 11) { 
      onNext({ cpf: digits });
    } else {
      alert('O CPF deve ter 11 dígitos.');
    }
  };

  return (
    <div className="esp-flow-step esp-flow-input-step">
      <div className="esp-flow-chat-bubble">
        Muito bem! Agora preciso do seu <strong>CPF</strong>!
      </div>
      <form onSubmit={handleSubmit} className="esp-exam-subscription-form">
        <input 
          type="text" 
          className="esp-exam-subscription-input"
          placeholder="000.000.000-00" 
          value={cpf} 
          onChange={handleChange}
          required
        />
        <div className="esp-exam-subscription-required-label">Campo obrigatório</div>
        <button type="submit" className="esp-exam-subscription-submit-button">→</button>
      </form>
    </div>
  );
};

const StepGetBirthDate = ({ onNext, initialData = {} }) => {
    const [date, setDate] = useState('');

    useEffect(() => {
      if (initialData.birthDate) {
        setDate(initialData.birthDate);
      }
    }, [initialData.birthDate]);

    const handleSubmit = (e) => {
      e.preventDefault();
      if (date) {
        onNext({ birthDate: date });
      } else {
        alert('Data de nascimento é obrigatória.');
      }
    };
  
    return (
      <div className="esp-flow-step esp-flow-input-step">
        <div className="esp-flow-chat-bubble">
          Qual sua data de nascimento?
        </div>
        <form onSubmit={handleSubmit} className="esp-exam-subscription-form">
          <input 
            type="date" 
            className="esp-exam-subscription-input"
            value={date} 
            onChange={(e) => setDate(e.target.value)} 
            required
          />
          <div className="esp-exam-subscription-required-label">Campo obrigatório</div>
          <button type="submit" className="esp-exam-subscription-submit-button">→</button>
        </form>
      </div>
    );
};

const StepReviewData = ({ onNext, data }) => (
  <div className="esp-flow-step">
    <div className="esp-flow-chat-bubble">
      Olá [Nome do Usuário - Simulado]!
      <br />
      De acordo com o seu cadastro da Receita Federal, você nasceu no dia [Data - Simulado] e sua mãe chama-se [Nome da Mãe - Simulado].
    </div>
    <div className="esp-flow-info-box">
      Caso seus dados pessoais não estejam corretos, prossiga sua inscrição normalmente e entre em contato com a Receita Federal para proceder a atualização necessária.
      <br />
      Em caso de dúvidas, entre em contato com a Central de Atendimento pelo telefone <strong>0800-616161</strong>.
    </div>
    <div className="esp-flow-info-box">
      A partir de agora vou te chamar apenas de [Primeiro Nome - Simulado].
    </div>
    <div className="esp-flow-actions">
      <button onClick={() => alert('Sair logic goes here')} className="esp-btn-secondary">SAIR</button>
      <button onClick={() => onNext()} className="esp-btn-primary">PRÓXIMO →</button>
    </div>
  </div>
);

const StepGetFatherName = ({ onNext, onPrev, initialData = {} }) => {
    const [fatherName, setFatherName] = useState('');
    const [skip, setSkip] = useState(false);

    useEffect(() => {
      if (initialData.fatherName) {
        const isSkipped = initialData.fatherName === 'Não Declarado';
        setSkip(isSkipped);
        setFatherName(isSkipped ? '' : initialData.fatherName);
      }
    }, [initialData.fatherName]);
  
    const handleSubmit = (e) => {
      e.preventDefault();
      if (fatherName || skip) {
        onNext({ fatherName: skip ? 'Não Declarado' : fatherName });
      } else {
        alert('O nome do pai é obrigatório, a menos que você marque "Não quero declarar".');
      }
    };
  
    return (
      <div className="esp-flow-step esp-flow-input-step">
        <div className="esp-flow-chat-bubble">
          Já sei seu nome, o nome de sua mãe e sua data de nascimento. E o seu pai, qual o nome dele?
        </div>
        <form onSubmit={handleSubmit} className="esp-exam-subscription-form esp-exam-subscription-form-complex">
          <label className="esp-exam-subscription-label">Nome Completo do Pai</label>
          <input 
            type="text" 
            className="esp-exam-subscription-input"
            value={fatherName} 
            onChange={(e) => setFatherName(e.target.value)} 
            disabled={skip}
          />
          <label className="esp-exam-subscription-checkbox-label">
            <input 
              type="checkbox" 
              checked={skip} 
              onChange={(e) => {
                setSkip(e.target.checked);
                setFatherName('');
              }}
            />
            Não quero declarar
          </label>
          <div className="esp-flow-actions esp-flow-back-next">
              <button type="button" onClick={() => onPrev()} className="esp-btn-secondary">← VOLTAR</button>
              <button type="submit" className="esp-btn-primary">PRÓXIMO →</button>
          </div>
        </form>
      </div>
    );
};

const StepReadyToStart = ({ onNext }) => (
    <div className="esp-flow-step">
      <div className="esp-flow-chat-bubble">
        Ok. Tudo pronto para começarmos sua inscrição?
      </div>
      <div className="esp-flow-actions">
        <button onClick={() => onNext()} className="esp-btn-primary esp-large">INICIAR A INSCRIÇÃO</button>
      </div>
    </div>
);

const StepSchoolType = ({ onNext, onPrev, initialData = {} }) => {
  const [selected, setSelected] = useState('');

  useEffect(() => {
    if (initialData.schoolType) {
      setSelected(initialData.schoolType);
    }
  }, [initialData.schoolType]);

  const handleSubmit = () => {
    if (selected) {
      onNext({ schoolType: selected });
    } else {
      alert('Por favor, selecione uma opção.');
    }
  };

  return (
    <div className="esp-flow-step">
      <div className="esp-flow-chat-bubble">
        Qual o tipo de escola que você frequentou?
      </div>
      <div className="esp-radio-options">
        <label className="esp-radio-option">
          <input 
            type="radio" 
            name="schoolType" 
            value="publica"
            checked={selected === 'publica'}
            onChange={(e) => setSelected(e.target.value)}
          />
          <span>Somente em escola pública.</span>
        </label>
        <label className="esp-radio-option">
          <input 
            type="radio" 
            name="schoolType" 
            value="publica_privada_sem_bolsa"
            checked={selected === 'publica_privada_sem_bolsa'}
            onChange={(e) => setSelected(e.target.value)}
          />
          <span>Parte em escola pública e parte em escola privada SEM bolsa de estudo integral.</span>
        </label>
        <label className="esp-radio-option">
          <input 
            type="radio" 
            name="schoolType" 
            value="publica_privada_com_bolsa"
            checked={selected === 'publica_privada_com_bolsa'}
            onChange={(e) => setSelected(e.target.value)}
          />
          <span>Parte em escola pública e parte em escola privada COM bolsa de estudo integral.</span>
        </label>
        <label className="esp-radio-option">
          <input 
            type="radio" 
            name="schoolType" 
            value="privada_sem_bolsa"
            checked={selected === 'privada_sem_bolsa'}
            onChange={(e) => setSelected(e.target.value)}
          />
          <span>Somente escola privada SEM bolsa de estudo integral.</span>
        </label>
      </div>
      <div className="esp-flow-actions esp-flow-back-next">
        <button type="button" onClick={() => onPrev()} className="esp-btn-secondary">← VOLTAR</button>
        <button onClick={handleSubmit} className="esp-btn-primary">PRÓXIMO →</button>
      </div>
    </div>
  );
};

const StepHighSchoolStatus = ({ onNext, onPrev, initialData = {} }) => {
  const [selected, setSelected] = useState('');

  useEffect(() => {
    if (initialData.highSchoolStatus) {
      setSelected(initialData.highSchoolStatus);
    }
  }, [initialData.highSchoolStatus]);

  const handleSubmit = () => {
    if (selected) {
      onNext({ highSchoolStatus: selected });
    } else {
      alert('Por favor, selecione uma opção.');
    }
  };

  return (
    <div className="esp-flow-step">
      <div className="esp-flow-chat-bubble">
        Qual a atual situação do seu ensino médio?
      </div>
      <div className="esp-radio-options">
        <label className="esp-radio-option">
          <input 
            type="radio" 
            name="hsStatus" 
            value="concluido"
            checked={selected === 'concluido'}
            onChange={(e) => setSelected(e.target.value)}
          />
          <span>Já concluí o ensino médio.</span>
        </label>
        <label className="esp-radio-option">
          <input 
            type="radio" 
            name="hsStatus" 
            value="cursando_ultimo"
            checked={selected === 'cursando_ultimo'}
            onChange={(e) => setSelected(e.target.value)}
          />
          <span>Estou cursando a última série/ano do ensino médio.</span>
        </label>
        <label className="esp-radio-option">
          <input 
            type="radio" 
            name="hsStatus" 
            value="cursando_nao_ultimo"
            checked={selected === 'cursando_nao_ultimo'}
            onChange={(e) => setSelected(e.target.value)}
          />
          <span>Estou cursando o ensino médio, mas não estou na última série/ano.</span>
        </label>
        <label className="esp-radio-option">
          <input 
            type="radio" 
            name="hsStatus" 
            value="nao_cursando"
            checked={selected === 'nao_cursando'}
            onChange={(e) => setSelected(e.target.value)}
          />
          <span>Não estou cursando e não concluí o ensino médio.</span>
        </label>
      </div>
      <div className="esp-flow-actions esp-flow-back-next">
        <button type="button" onClick={() => onPrev()} className="esp-btn-secondary">← VOLTAR</button>
        <button onClick={handleSubmit} className="esp-btn-primary">PRÓXIMO →</button>
      </div>
    </div>
  );
};

const StepLanguageChoice = ({ onNext, onPrev, initialData = {} }) => {
  const [selected, setSelected] = useState('');

  useEffect(() => {
    if (initialData.language) {
      setSelected(initialData.language);
    }
  }, [initialData.language]);

  const handleSubmit = () => {
    if (selected) {
      onNext({ language: selected });
    } else {
      alert('Por favor, selecione um idioma.');
    }
  };

  return (
    <div className="esp-flow-step">
      <div className="esp-flow-chat-bubble">
        Você pode escolher Inglês ou Espanhol para realizar a prova de língua estrangeira do Enem. Qual dos dois idiomas você escolhe?
      </div>
      <div className="esp-choice-cards">
        <div 
          className={`esp-choice-card ${selected === 'ingles' ? 'esp-selected' : ''}`}
          onClick={() => setSelected('ingles')}
        >
          <div className="esp-choice-label">Inglês</div>
          <div className="esp-choice-radio">
            <input type="radio" checked={selected === 'ingles'} readOnly />
          </div>
        </div>
        <div 
          className={`esp-choice-card ${selected === 'espanhol' ? 'esp-selected' : ''}`}
          onClick={() => setSelected('espanhol')}
        >
          <div className="esp-choice-label">Espanhol</div>
          <div className="esp-choice-radio">
            <input type="radio" checked={selected === 'espanhol'} readOnly />
          </div>
        </div>
      </div>
      <div className="esp-flow-actions esp-flow-back-next">
        <button type="button" onClick={() => onPrev()} className="esp-btn-secondary">← VOLTAR</button>
        <button onClick={handleSubmit} className="esp-btn-primary">PRÓXIMO →</button>
      </div>
    </div>
  );
};

const StepAccessibilityNeeds = ({ onNext, onPrev, initialData = {} }) => {
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (initialData.accessibilityNeeds) {
      setSelected(initialData.accessibilityNeeds);
    }
  }, [initialData.accessibilityNeeds]);

  const needs = [
    'Surdocegueira', 'Baixa visão', 'Cegueira',
    'Visão monocular', 'Deficiência auditiva', 'Surdez',
    'Autismo', 'Deficiência intelectual (Mental)', 'Discalculia',
    'Dislexia', 'Déficit de atenção', 'Deficiência física'
  ];

  const toggleNeed = (need) => {
    if (selected.includes(need)) {
      setSelected(selected.filter(n => n !== need));
    } else {
      setSelected([...selected, need]);
    }
  };

  const handleSubmit = () => {
    onNext({ accessibilityNeeds: selected });
  };

  return (
    <div className="esp-flow-step">
      <div className="esp-flow-chat-bubble">
        Qual a sua necessidade de atendimento especializado?
      </div>
      <div className="esp-checkbox-grid">
        {needs.map((need) => (
          <label key={need} className="esp-checkbox-option">
            <input 
              type="checkbox" 
              checked={selected.includes(need)}
              onChange={() => toggleNeed(need)}
            />
            <span>{need}</span>
          </label>
        ))}
      </div>
      <div className="esp-flow-actions esp-flow-back-next">
        <button type="button" onClick={() => onPrev()} className="esp-btn-secondary">← VOLTAR</button>
        <button onClick={handleSubmit} className="esp-btn-primary">PRÓXIMO →</button>
      </div>
    </div>
  );
};

const StepAccessibilityResources = ({ onNext, onPrev, initialData = {} }) => {
  const [selected, setSelected] = useState('');

  useEffect(() => {
    if (initialData.accessibilityResources) {
      setSelected(initialData.accessibilityResources);
    }
  }, [initialData.accessibilityResources]);

  const handleSubmit = () => {
    if (selected) {
      onNext({ accessibilityResources: selected });
    } else {
      alert('Por favor, selecione uma opção.');
    }
  };

  return (
    <div className="esp-flow-step">
      <div className="esp-flow-chat-bubble">
        Você sabia que o Enem oferece recursos de acessibilidade para quem tem deficiência ou precisa de condições especiais para fazer a prova?
        <br /><br />
        Você precisa de algum tipo de recurso de acessibilidade para os dois dias de prova?
      </div>
      <div className="esp-choice-cards">
        <div 
          className={`esp-choice-card ${selected === 'preciso' ? 'esp-selected' : ''}`}
          onClick={() => setSelected('preciso')}
        >
          <div className="esp-choice-label">Preciso</div>
          <div className="esp-choice-radio">
            <input type="radio" checked={selected === 'preciso'} readOnly />
          </div>
        </div>
        <div 
          className={`esp-choice-card ${selected === 'nao_preciso' ? 'esp-selected' : ''}`}
          onClick={() => setSelected('nao_preciso')}
        >
          <div className="esp-choice-label">Não preciso</div>
          <div className="esp-choice-radio">
            <input type="radio" checked={selected === 'nao_preciso'} readOnly />
          </div>
        </div>
      </div>
      <div className="esp-flow-actions esp-flow-back-next">
        <button type="button" onClick={() => onPrev()} className="esp-btn-secondary">← VOLTAR</button>
        <button onClick={handleSubmit} className="esp-btn-primary">PRÓXIMO →</button>
      </div>
    </div>
  );
};

const StepCEP = ({ onNext, onPrev, initialData = {} }) => {
  const [cep, setCep] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (initialData.cep) {
      setCep(initialData.cep);
    }
  }, [initialData.cep]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cep.length !== 8) {
      alert('O CEP deve ter 8 dígitos.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      if (!response.ok) {
        throw new Error('Erro ao buscar o CEP');
      }
      
      const data = await response.json();
      
      if (data.erro) {
        throw new Error('CEP não encontrado');
      }

      const formattedAddress = `${data.logradouro}, ${data.bairro}, ${data.localidade} - ${data.uf}`;
      
      onNext({ 
        cep,
        addressData: {
          logradouro: data.logradouro,
          bairro: data.bairro,
          localidade: data.localidade,
          uf: data.uf,
          formattedAddress
        }
      });
    } catch (err) {
      setError(err.message || 'Erro ao buscar endereço.');
      alert(`Erro: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="esp-flow-step esp-flow-input-step">
      <div className="esp-flow-chat-bubble">
        Qual o CEP do local onde você mora?
      </div>
      <div className="esp-flow-info-bubble">
        Se você não tem certeza de qual é o seu CEP, o site dos Correios pode te ajudar.
      </div>
      <form onSubmit={handleSubmit} className="esp-exam-subscription-form">
        <label className="esp-exam-subscription-label">CEP*</label>
        <input 
          type="text" 
          className="esp-exam-subscription-input"
          placeholder="00000-000"
          value={cep} 
          onChange={(e) => setCep(e.target.value.replace(/\D/g, '').substring(0, 8))}
          required
          disabled={loading}
        />
        {error && <div className="esp-error-message" style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{error}</div>}
        <div className="esp-flow-actions esp-flow-back-next">
          <button type="button" onClick={() => onPrev()} className="esp-btn-secondary" disabled={loading}>← VOLTAR</button>
          <button type="submit" className="esp-btn-primary" disabled={loading}>
            {loading ? 'Buscando...' : 'PRÓXIMO →'}
          </button>
        </div>
      </form>
    </div>
  );
};

const StepAddressInfo = ({ onNext, onPrev, data, initialData = {} }) => {
  const [numero, setNumero] = useState('');
  const [complemento, setComplemento] = useState('');

  useEffect(() => {
    if (initialData.addressNumber) setNumero(initialData.addressNumber);
    if (initialData.addressComplement) setComplemento(initialData.addressComplement);
  }, [initialData.addressNumber, initialData.addressComplement]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (numero) {
      onNext({ addressNumber: numero, addressComplement: complemento });
    } else {
      alert('O número é obrigatório.');
    }
  };

  const addressData = data.addressData || initialData.addressData || {};
  const formattedAddress = addressData.formattedAddress || '[Endereço - Simulado]';
  const cep = data.cep || initialData.cep || '[CEP - Simulado]';

  return (
    <div className="esp-flow-step esp-flow-input-step">
      <div className="esp-flow-chat-bubble">
        Você mora em {formattedAddress}, legal!
        <br /><br />
        Pelo CEP que você me informou ({cep}), seu endereço é {formattedAddress}.
        <br /><br />
        Complete as informações abaixo para prosseguirmos.
      </div>
      <form onSubmit={handleSubmit} className="esp-exam-subscription-form esp-exam-subscription-form-complex">
        <div className="esp-form-row">
          <div className="esp-form-field">
            <label className="esp-exam-subscription-label">Número*</label>
            <input 
              type="text" 
              className="esp-exam-subscription-input"
              value={numero} 
              onChange={(e) => setNumero(e.target.value)} 
              required
            />
          </div>
          <div className="esp-form-field">
            <label className="esp-exam-subscription-label">Complemento</label>
            <input 
              type="text" 
              className="esp-exam-subscription-input"
              value={complemento} 
              onChange={(e) => setComplemento(e.target.value)} 
            />
          </div>
        </div>
        <div className="esp-flow-actions esp-flow-back-next">
          <button type="button" onClick={() => onPrev()} className="esp-btn-secondary">← VOLTAR</button>
          <button type="submit" className="esp-btn-primary">PRÓXIMO →</button>
        </div>
      </form>
    </div>
  );
};

const StepDocumentInfo = ({ onNext, onPrev, initialData = {} }) => {
  const [documentNumber, setDocumentNumber] = useState('');
  const [uf, setUf] = useState('');
  const [issuingAgency, setIssuingAgency] = useState('');

  const brazilianStates = [
    { sigla: 'AC', nome: 'Acre' }, { sigla: 'AL', nome: 'Alagoas' }, { sigla: 'AP', nome: 'Amapá' },
    { sigla: 'AM', nome: 'Amazonas' }, { sigla: 'BA', nome: 'Bahia' }, { sigla: 'CE', nome: 'Ceará' },
    { sigla: 'DF', nome: 'Distrito Federal' }, { sigla: 'ES', nome: 'Espírito Santo' }, { sigla: 'GO', nome: 'Goiás' },
    { sigla: 'MA', nome: 'Maranhão' }, { sigla: 'MT', nome: 'Mato Grosso' }, { sigla: 'MS', nome: 'Mato Grosso do Sul' },
    { sigla: 'MG', nome: 'Minas Gerais' }, { sigla: 'PA', nome: 'Pará' }, { sigla: 'PB', nome: 'Paraíba' },
    { sigla: 'PR', nome: 'Paraná' }, { sigla: 'PE', nome: 'Pernambuco' }, { sigla: 'PI', nome: 'Piauí' },
    { sigla: 'RJ', nome: 'Rio de Janeiro' }, { sigla: 'RN', nome: 'Rio Grande do Norte' }, { sigla: 'RS', nome: 'Rio Grande do Sul' },
    { sigla: 'RO', nome: 'Rondônia' }, { sigla: 'RR', nome: 'Roraima' }, { sigla: 'SC', nome: 'Santa Catarina' },
    { sigla: 'SP', nome: 'São Paulo' }, { sigla: 'SE', nome: 'Sergipe' }, { sigla: 'TO', nome: 'Tocantins' }
  ];

  useEffect(() => {
    if (initialData.documentNumber) setDocumentNumber(initialData.documentNumber);
    if (initialData.uf) setUf(initialData.uf);
    if (initialData.issuingAgency) setIssuingAgency(initialData.issuingAgency);
  }, [initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (documentNumber && uf && issuingAgency) {
      onNext({ documentNumber, uf, issuingAgency });
    } else {
      alert('Todos os campos são obrigatórios.');
    }
  };

  return (
    <div className="esp-flow-step esp-flow-input-step">
      <div className="esp-flow-chat-bubble">Agora preciso de outras informações.</div>
      <form onSubmit={handleSubmit} className="esp-exam-subscription-form esp-exam-subscription-form-complex">
        <div className="esp-form-row">
          <div className="esp-form-field esp-form-field-wide">
            <label className="esp-exam-subscription-label">Número do documento*</label>
            <input 
              type="text" 
              className="esp-exam-subscription-input"
              value={documentNumber} 
              onChange={(e) => setDocumentNumber(e.target.value)}
              required
            />
          </div>
          <div className="esp-form-field">
            <label className="esp-exam-subscription-label">UF*</label>
            <select className="esp-exam-subscription-input" value={uf} onChange={(e) => setUf(e.target.value)} required>
              <option value="">Selecione</option>
              {brazilianStates.map(s => <option key={s.sigla} value={s.sigla}>{s.sigla}</option>)}
            </select>
          </div>
        </div>
        <div className="esp-form-field">
          <label className="esp-exam-subscription-label">Órgão Expedidor*</label>
          <select className="esp-exam-subscription-input" value={issuingAgency} onChange={(e) => setIssuingAgency(e.target.value)} required>
            <option value="">Selecione</option>
            <option value="SSP">SSP</option>
            <option value="DETRAN">DETRAN</option>
          </select>
        </div>
        <div className="esp-flow-actions esp-flow-back-next">
          <button type="button" onClick={() => onPrev()} className="esp-btn-secondary">← VOLTAR</button>
          <button type="submit" className="esp-btn-primary">PRÓXIMO →</button>
        </div>
      </form>
    </div>
  );
};

const StepBirthLocation = ({ onNext, onPrev, initialData = {} }) => {
  const [uf, setUf] = useState('');
  const [municipality, setMunicipality] = useState('');
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  const brazilianStates = [
    { sigla: 'AC', nome: 'Acre' }, { sigla: 'AL', nome: 'Alagoas' }, { sigla: 'AP', nome: 'Amapá' },
    { sigla: 'AM', nome: 'Amazonas' }, { sigla: 'BA', nome: 'Bahia' }, { sigla: 'CE', nome: 'Ceará' },
    { sigla: 'DF', nome: 'Distrito Federal' }, { sigla: 'ES', nome: 'Espírito Santo' }, { sigla: 'GO', nome: 'Goiás' },
    { sigla: 'MA', nome: 'Maranhão' }, { sigla: 'MT', nome: 'Mato Grosso' }, { sigla: 'MS', nome: 'Mato Grosso do Sul' },
    { sigla: 'MG', nome: 'Minas Gerais' }, { sigla: 'PA', nome: 'Pará' }, { sigla: 'PB', nome: 'Paraíba' },
    { sigla: 'PR', nome: 'Paraná' }, { sigla: 'PE', nome: 'Pernambuco' }, { sigla: 'PI', nome: 'Piauí' },
    { sigla: 'RJ', nome: 'Rio de Janeiro' }, { sigla: 'RN', nome: 'Rio Grande do Norte' }, { sigla: 'RS', nome: 'Rio Grande do Sul' },
    { sigla: 'RO', nome: 'Rondônia' }, { sigla: 'RR', nome: 'Roraima' }, { sigla: 'SC', nome: 'Santa Catarina' },
    { sigla: 'SP', nome: 'São Paulo' }, { sigla: 'SE', nome: 'Sergipe' }, { sigla: 'TO', nome: 'Tocantins' }
  ];

  useEffect(() => {
    if (initialData.birthUf) setUf(initialData.birthUf);
    if (initialData.birthMunicipality) setMunicipality(initialData.birthMunicipality);
  }, [initialData]);

  useEffect(() => {
    if (uf) {
      setLoadingCities(true);
      fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios`)
        .then(response => response.json())
        .then(data => {
          const sortedCities = data.map(city => city.nome).sort((a, b) => a.localeCompare(b, 'pt-BR'));
          setCities(sortedCities);
          setLoadingCities(false);
        })
        .catch(() => {
          setCities([]);
          setLoadingCities(false);
        });
    } else {
      setCities([]);
    }
  }, [uf]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (uf && municipality) {
      onNext({ birthUf: uf, birthMunicipality: municipality });
    } else {
      alert('Todos os campos são obrigatórios.');
    }
  };

  return (
    <div className="esp-flow-step esp-flow-input-step">
      <div className="esp-flow-chat-bubble">Em qual Estado e cidade você nasceu?</div>
      <form onSubmit={handleSubmit} className="esp-exam-subscription-form esp-exam-subscription-form-complex">
        <div className="esp-form-row">
          <div className="esp-form-field">
            <label className="esp-exam-subscription-label">UF*</label>
            <select className="esp-exam-subscription-input" value={uf} onChange={(e) => setUf(e.target.value)} required>
              <option value="">Selecione</option>
              {brazilianStates.map(s => <option key={s.sigla} value={s.sigla}>{s.sigla} - {s.nome}</option>)}
            </select>
          </div>
          <div className="esp-form-field esp-form-field-wide">
            <label className="esp-exam-subscription-label">Município*</label>
            <select className="esp-exam-subscription-input" value={municipality} onChange={(e) => setMunicipality(e.target.value)} required disabled={!uf || loadingCities}>
              <option value="">{loadingCities ? 'Carregando...' : 'Selecione'}</option>
              {cities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="esp-flow-actions esp-flow-back-next">
          <button type="button" onClick={() => onPrev()} className="esp-btn-secondary">← VOLTAR</button>
          <button type="submit" className="esp-btn-primary" disabled={!uf || !municipality || loadingCities}>PRÓXIMO →</button>
        </div>
      </form>
    </div>
  );
};

const StepPersonalInfo = ({ onNext, onPrev, initialData = {} }) => {
  const [gender, setGender] = useState('');
  const [race, setRace] = useState('');
  const [maritalStatus, setMaritalStatus] = useState('');
  const [nationality, setNationality] = useState('');

  useEffect(() => {
    if (initialData.gender) setGender(initialData.gender);
    if (initialData.race) setRace(initialData.race);
    if (initialData.maritalStatus) setMaritalStatus(initialData.maritalStatus);
    if (initialData.nationality) setNationality(initialData.nationality);
  }, [initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (gender && race && maritalStatus && nationality) {
      onNext({ gender, race, maritalStatus, nationality });
    } else {
      alert('Todos os campos são obrigatórios.');
    }
  };

  return (
    <div className="esp-flow-step esp-flow-input-step">
      <div className="esp-flow-chat-bubble">Me fale um pouco mais sobre você.</div>
      <form onSubmit={handleSubmit} className="esp-exam-subscription-form esp-exam-subscription-form-complex">
        <div className="esp-form-row esp-form-row-3">
          <div className="esp-form-field">
            <label className="esp-exam-subscription-label">Sexo*</label>
            <select className="esp-exam-subscription-input" value={gender} onChange={(e) => setGender(e.target.value)} required>
              <option value="">Selecione</option>
              <option value="Feminino">Feminino</option>
              <option value="Masculino">Masculino</option>
            </select>
          </div>
          <div className="esp-form-field">
            <label className="esp-exam-subscription-label">Cor/Raça*</label>
            <select className="esp-exam-subscription-input" value={race} onChange={(e) => setRace(e.target.value)} required>
              <option value="">Selecione</option>
              <option value="Branca">Branca</option><option value="Preta">Preta</option><option value="Parda">Parda</option>
            </select>
          </div>
          <div className="esp-form-field">
            <label className="esp-exam-subscription-label">Estado Civil*</label>
            <select className="esp-exam-subscription-input" value={maritalStatus} onChange={(e) => setMaritalStatus(e.target.value)} required>
              <option value="">Selecione</option>
              <option value="Solteiro(a)">Solteiro(a)</option><option value="Casado(a)">Casado(a)</option>
            </select>
          </div>
        </div>
        <div className="esp-form-field">
          <label className="esp-exam-subscription-label">Nacionalidade*</label>
          <select className="esp-exam-subscription-input" value={nationality} onChange={(e) => setNationality(e.target.value)} required>
            <option value="">Selecione</option>
            <option value="Brasileira">Brasileira</option>
          </select>
        </div>
        <div className="esp-flow-actions esp-flow-back-next">
          <button type="button" onClick={() => onPrev()} className="esp-btn-secondary">← VOLTAR</button>
          <button type="submit" className="esp-btn-primary">PRÓXIMO →</button>
        </div>
      </form>
    </div>
  );
};

const StepSocioeconomicIntro = ({ onNext, onPrev }) => {
  return (
    <div className="esp-flow-step">
      <div className="esp-flow-chat-bubble">
        Vamos iniciar agora o preenchimento do Questionário Socioeconômico. São 25 perguntas.
        <br /><br />
        Por isso, é muito importante que você responda ao Questionário de acordo com a sua real situação.
      </div>
      <div className="esp-flow-actions esp-flow-back-next">
        <button type="button" onClick={() => onPrev()} className="esp-btn-secondary">← VOLTAR</button>
        <button onClick={() => onNext()} className="esp-btn-primary">PRÓXIMO →</button>
      </div>
    </div>
  );
};

const StepNIS = ({ onNext, onPrev, initialData = {} }) => {
  const [nis, setNis] = useState('');
  const [noNis, setNoNis] = useState(false);

  useEffect(() => {
    if (initialData.nis) {
      const isNone = initialData.nis === 'Não tenho';
      setNoNis(isNone);
      setNis(isNone ? '' : initialData.nis);
    }
  }, [initialData.nis]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (nis || noNis) {
      onNext({ nis: noNis ? 'Não tenho' : nis });
    } else {
      alert('Por favor, informe o NIS ou marque que não possui.');
    }
  };

  return (
    <div className="esp-flow-step esp-flow-input-step">
      <div className="esp-flow-chat-bubble">Seu Número de Identificação Social (NIS).</div>
      <form onSubmit={handleSubmit} className="esp-exam-subscription-form esp-exam-subscription-form-complex">
        <input type="text" className="esp-exam-subscription-input" value={nis} onChange={(e) => setNis(e.target.value.replace(/\D/g, ''))} disabled={noNis} />
        <label className="esp-exam-subscription-checkbox-label">
          <input type="checkbox" checked={noNis} onChange={(e) => { setNoNis(e.target.checked); if (e.target.checked) setNis(''); }} /> Não tenho NIS
        </label>
        <div className="esp-flow-actions esp-flow-back-next">
          <button type="button" onClick={() => onPrev()} className="esp-btn-secondary">← VOLTAR</button>
          <button type="submit" className="esp-btn-primary">PRÓXIMO →</button>
        </div>
      </form>
    </div>
  );
};

const StepEducationType = ({ onNext, onPrev, initialData = {} }) => {
  const [educationType, setEducationType] = useState('');
  const [completionYear, setCompletionYear] = useState('');

  useEffect(() => {
    if (initialData.educationType) setEducationType(initialData.educationType);
    if (initialData.completionYear) setCompletionYear(initialData.completionYear);
  }, [initialData]);

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const startYear = 1970;
    const years = [];
    for (let year = currentYear + 2; year >= startYear; year--) { years.push(year); }
    return years;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (educationType && completionYear) {
      onNext({ educationType, completionYear });
    } else {
      alert('Todos os campos são obrigatórios.');
    }
  };

  return (
    <div className="esp-flow-step esp-flow-input-step">
      <div className="esp-flow-chat-bubble">Qual o tipo de ensino e o ano de conclusão?</div>
      <form onSubmit={handleSubmit} className="esp-exam-subscription-form esp-exam-subscription-form-complex">
        <div className="esp-form-row">
          <div className="esp-form-field">
            <label className="esp-exam-subscription-label">Tipo de Ensino*</label>
            <select className="esp-exam-subscription-input" value={educationType} onChange={(e) => setEducationType(e.target.value)} required>
              <option value="">Selecione</option>
              <option value="Ensino Regular">Ensino Regular</option>
              <option value="EJA">Educação de Jovens e Adultos</option>
            </select>
          </div>
          <div className="esp-form-field">
            <label className="esp-exam-subscription-label">Ano de Conclusão*</label>
            <select className="esp-exam-subscription-input" value={completionYear} onChange={(e) => setCompletionYear(e.target.value)} required>
              <option value="">Selecione</option>
              {generateYearOptions().map(y => <option key={y} value={y.toString()}>{y}</option>)}
            </select>
          </div>
        </div>
        <div className="esp-flow-actions esp-flow-back-next">
          <button type="button" onClick={() => onPrev()} className="esp-btn-secondary">← VOLTAR</button>
          <button type="submit" className="esp-btn-primary">PRÓXIMO →</button>
        </div>
      </form>
    </div>
  );
};

const StepEncceja = ({ onNext, onPrev, initialData = {} }) => {
  const [selected, setSelected] = useState('');

  useEffect(() => {
    if (initialData.encceja) setSelected(initialData.encceja);
  }, [initialData.encceja]);

  const handleSubmit = () => {
    if (selected) onNext({ encceja: selected });
    else alert('Selecione.');
  };

  return (
    <div className="esp-flow-step">
      <div className="esp-flow-chat-bubble">Certificação pelo Encceja?</div>
      <div className="esp-choice-cards">
        <div className={`esp-choice-card ${selected === 'sim' ? 'esp-selected' : ''}`} onClick={() => setSelected('sim')}>Sim</div>
        <div className={`esp-choice-card ${selected === 'nao' ? 'esp-selected' : ''}`} onClick={() => setSelected('nao')}>Não</div>
      </div>
      <div className="esp-flow-actions esp-flow-back-next">
        <button type="button" onClick={() => onPrev()} className="esp-btn-secondary">← VOLTAR</button>
        <button onClick={handleSubmit} className="esp-btn-primary">PRÓXIMO →</button>
      </div>
    </div>
  );
};

const StepInternetAccess = ({ onNext, onPrev, initialData = {} }) => {
  const [selected, setSelected] = useState('');

  useEffect(() => {
    if (initialData.internetAccess) setSelected(initialData.internetAccess);
  }, [initialData.internetAccess]);

  const handleSubmit = () => {
    if (selected) onNext({ internetAccess: selected });
    else alert('Selecione.');
  };

  return (
    <div className="esp-flow-step">
      <div className="esp-flow-chat-bubble">Acesso à Internet?</div>
      <div className="esp-answer-options">
        <div className={`esp-answer-option ${selected === 'nao' ? 'esp-selected' : ''}`} onClick={() => setSelected('nao')}>Não</div>
        <div className={`esp-answer-option ${selected === 'sim' ? 'esp-selected' : ''}`} onClick={() => setSelected('sim')}>Sim</div>
      </div>
      <div className="esp-flow-actions esp-flow-back-next">
        <button type="button" onClick={() => onPrev()} className="esp-btn-secondary">← VOLTAR</button>
        <button onClick={handleSubmit} className="esp-btn-primary">PRÓXIMO →</button>
      </div>
    </div>
  );
};

const StepPhotoUpload = ({ onNext, onPrev, initialData = {} }) => {
  const [selectedFile, setSelectedFile] = useState(null);

  const handleSubmit = () => {
    if (selectedFile) onNext({ photo: selectedFile.name });
    else alert('Selecione uma foto.');
  };

  return (
    <div className="esp-flow-step">
      <div className="esp-photo-upload-container">
        <input type="file" id="photo-upload" accept="image/*" onChange={(e) => setSelectedFile(e.target.files[0])} style={{ display: 'none' }} />
        <label htmlFor="photo-upload" className="esp-photo-upload-area">
          {selectedFile ? <div>{selectedFile.name}</div> : <div>📷 Escolha foto</div>}
        </label>
      </div>
      <div className="esp-flow-actions esp-flow-back-next">
        <button type="button" onClick={() => onPrev()} className="esp-btn-secondary">VOLTAR</button>
        <button onClick={handleSubmit} className="esp-btn-primary">PRÓXIMO →</button>
      </div>
    </div>
  );
};

const StepExamLocation = ({ onNext, onPrev, initialData = {} }) => {
  const [uf, setUf] = useState('');
  const [municipality, setMunicipality] = useState('');
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  const brazilianStates = [
    { sigla: 'AC', nome: 'Acre' }, { sigla: 'AL', nome: 'Alagoas' }, { sigla: 'AP', nome: 'Amapá' },
    { sigla: 'AM', nome: 'Amazonas' }, { sigla: 'BA', nome: 'Bahia' }, { sigla: 'CE', nome: 'Ceará' },
    { sigla: 'DF', nome: 'Distrito Federal' }, { sigla: 'ES', nome: 'Espírito Santo' }, { sigla: 'GO', nome: 'Goiás' },
    { sigla: 'MA', nome: 'Maranhão' }, { sigla: 'MT', nome: 'Mato Grosso' }, { sigla: 'MS', nome: 'Mato Grosso do Sul' },
    { sigla: 'MG', nome: 'Minas Gerais' }, { sigla: 'PA', nome: 'Pará' }, { sigla: 'PB', nome: 'Paraíba' },
    { sigla: 'PR', nome: 'Paraná' }, { sigla: 'PE', nome: 'Pernambuco' }, { sigla: 'PI', nome: 'Piauí' },
    { sigla: 'RJ', nome: 'Rio de Janeiro' }, { sigla: 'RN', nome: 'Rio Grande do Norte' }, { sigla: 'RS', nome: 'Rio Grande do Sul' },
    { sigla: 'RO', nome: 'Rondônia' }, { sigla: 'RR', nome: 'Roraima' }, { sigla: 'SC', nome: 'Santa Catarina' },
    { sigla: 'SP', nome: 'São Paulo' }, { sigla: 'SE', nome: 'Sergipe' }, { sigla: 'TO', nome: 'Tocantins' }
  ];

  useEffect(() => {
    if (initialData.examUf) setUf(initialData.examUf);
    if (initialData.examMunicipality) setMunicipality(initialData.examMunicipality);
  }, [initialData]);

  useEffect(() => {
    if (uf) {
      setLoadingCities(true);
      fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios`)
        .then(response => response.json())
        .then(data => {
          const sortedCities = data.map(city => city.nome).sort((a, b) => a.localeCompare(b, 'pt-BR'));
          setCities(sortedCities);
          setLoadingCities(false);
        })
        .catch(() => {
          setCities([]);
          setLoadingCities(false);
        });
    }
  }, [uf]);

  return (
    <div className="esp-flow-step esp-flow-input-step">
      <div className="esp-flow-chat-bubble">Escolha local da prova.</div>
      <form onSubmit={(e) => { e.preventDefault(); onNext({ examUf: uf, examMunicipality: municipality }); }} className="esp-exam-subscription-form esp-exam-subscription-form-complex">
        <div className="esp-form-row">
          <div className="esp-form-field">
            <select className="esp-exam-subscription-input" value={uf} onChange={(e) => setUf(e.target.value)} required>
              <option value="">UF</option>
              {brazilianStates.map(s => <option key={s.sigla} value={s.sigla}>{s.sigla}</option>)}
            </select>
          </div>
          <div className="esp-form-field esp-form-field-wide">
            <select className="esp-exam-subscription-input" value={municipality} onChange={(e) => setMunicipality(e.target.value)} required disabled={!uf || loadingCities}>
              <option value="">{loadingCities ? 'Carregando...' : 'Município'}</option>
              {cities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="esp-flow-actions esp-flow-back-next">
          <button type="button" onClick={() => onPrev()} className="esp-btn-secondary">VOLTAR</button>
          <button type="submit" className="esp-btn-primary">PRÓXIMO</button>
        </div>
      </form>
    </div>
  );
};

const StepExamTypeChoice = ({ onNext, onPrev, data, initialData = {} }) => {
  const [selected, setSelected] = useState('');

  useEffect(() => {
    if (initialData.examType) setSelected(initialData.examType);
  }, [initialData.examType]);

  const handleSubmit = () => {
    if (selected) onNext({ examType: selected });
    else alert('Selecione.');
  };

  return (
    <div className="esp-flow-step">
      <div className="esp-exam-type-cards">
        <div className={`esp-exam-type-card ${selected === 'impresso' ? 'esp-selected' : ''}`} onClick={() => setSelected('impresso')}>Impresso</div>
        <div className={`esp-exam-type-card ${selected === 'digital' ? 'esp-selected' : ''}`} onClick={() => setSelected('digital')}>Digital</div>
      </div>
      <div className="esp-flow-actions esp-flow-back-next">
        <button type="button" onClick={() => onPrev()} className="esp-btn-secondary">VOLTAR</button>
        <button onClick={handleSubmit} className="esp-btn-primary">PRÓXIMO</button>
      </div>
    </div>
  );
};

const StepContactInfo = ({ onNext, onPrev, initialData = {} }) => {
  const [landline, setLandline] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (initialData.landline) setLandline(initialData.landline);
    if (initialData.mobile) setMobile(initialData.mobile);
    if (initialData.email) setEmail(initialData.email);
  }, [initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onNext({ landline, mobile, email });
  };

  return (
    <div className="esp-flow-step esp-flow-input-step">
      <form onSubmit={handleSubmit} className="esp-exam-subscription-form esp-exam-subscription-form-complex">
        <input type="tel" placeholder="Fixo" className="esp-exam-subscription-input" value={landline} onChange={(e) => setLandline(e.target.value)} />
        <input type="tel" placeholder="Celular" className="esp-exam-subscription-input" value={mobile} onChange={(e) => setMobile(e.target.value)} required />
        <input type="email" placeholder="Email" className="esp-exam-subscription-input" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <div className="esp-flow-actions esp-flow-back-next">
          <button type="button" onClick={() => onPrev()} className="esp-btn-secondary">VOLTAR</button>
          <button type="submit" className="esp-btn-primary">PRÓXIMO</button>
        </div>
      </form>
    </div>
  );
};

const StepFinalReview = ({ onNext, onPrev, data, selectedExam }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Clean the data before sending
      const cleanDataToSend = cleanData(data);
      
      // Transform data to backend expected format, passing the selected exam
      const backendData = transformToBackendFormat(cleanDataToSend, selectedExam);
      
      // Add timestamp and any additional metadata if needed
      const submissionData = {
        ...backendData,
        submissionDate: new Date().toISOString(),
      };

      // Send to backend with transformed data
      const response = await fetch(
        `https://rvensino.com.br/get.php?action=examRegistration&data=${encodeURIComponent(JSON.stringify(submissionData))}`
      );

      if (!response.ok) {
        throw new Error(`Erro na requisição: ${response.status} ${response.statusText}`);
      }

      const responseText = await response.text();
      console.log('API Response:', responseText);
      console.log('Data sent to backend:', submissionData);

      // Save to localStorage for backup (save the original flat data)
      saveToLocalStorage(data);
      
      // Proceed to next step (success screen)
      onNext();
    } catch (error) {
      console.error('Erro ao enviar inscrição:', error);
      setSubmitError(error.message || 'Erro ao enviar inscrição. Por favor, tente novamente.');
      
      // Still save to localStorage even if API fails
      saveToLocalStorage(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="esp-flow-step">
      <div className="esp-flow-chat-bubble">Confira seus dados e clique em Enviar.</div>
      <div className="esp-review-section">
        <div className="esp-review-item">
          <div className="esp-review-label">CPF</div>
          <div className="esp-review-value">{data.cpf || '***'}</div>
        </div>
        {/* Add more review items as needed */}
      </div>
      
      {submitError && (
        <div className="esp-flow-info-bubble" style={{ background: '#ffe6e6', borderLeftColor: '#ff6767', color: '#ff6767' }}>
          {submitError}
        </div>
      )}

      <div className="esp-flow-actions esp-flow-back-next">
        <button 
          type="button" 
          onClick={() => onPrev()} 
          className="esp-btn-secondary"
          disabled={isSubmitting}
        >
          VOLTAR
        </button>
        <button 
          onClick={handleSubmit} 
          className="esp-btn-primary esp-btn-submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'ENVIANDO...' : 'ENVIAR INSCRIÇÃO →'}
        </button>
      </div>
    </div>
  );
};

// --- Calendar List View Component ---

const ExamCalendarList = ({ onExamSelect }) => {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchExams = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://rvensino.com.br/get.php?action=exams_calendar');
        
        if (!response.ok) {
          throw new Error(`Erro na requisição: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        
        if (data && Array.isArray(data)) {
          // Transform API data to match component format
          const transformedExams = data.map((exam, index) => {
            // Parse the date string from API (format: "2026-03-15 08:00:00")
            let date;
            try {
              // Remove time part and parse as date
              const dateStr = exam.date.split(' ')[0]; // Get "2026-03-15"
              date = new Date(dateStr + 'T00:00:00'); // Add time to avoid timezone issues
              
              // Validate date
              if (isNaN(date.getTime())) {
                console.warn('Invalid date:', exam.date);
                date = new Date(); // Fallback to current date
              }
            } catch (err) {
              console.warn('Error parsing date:', exam.date, err);
              date = new Date(); // Fallback to current date
            }
            
            const day = date.getDate();
            const month = date.toLocaleDateString('pt-BR', { month: 'short' }).toUpperCase();
            const weekday = date.toLocaleDateString('pt-BR', { weekday: 'short' }).toUpperCase();
            
            // Map status from API to component status
            let status = 'disponível';
            if (exam.status && exam.status.toLowerCase().includes('disponível')) {
              status = 'disponível';
            } else if (exam.status && exam.status.toLowerCase().includes('inscrito')) {
              status = 'inscrito';
            } else if (exam.status && exam.status.toLowerCase().includes('encerrado')) {
              status = 'encerrado';
            } else {
              status = 'não disponível';
            }
            
            return {
              id: index + 1,
              day,
              month,
              weekday,
              name: exam.name, // API returns "name" field, not "titulo"
              status,
              originalDate: exam.date // Keep original for reference
            };
          });
          
          setExams(transformedExams);
        } else {
          console.warn('Formato inesperado da resposta da API:', data);
          setExams([]);
        }
      } catch (err) {
        console.error('Erro ao buscar exames:', err);
        setError(err.message || 'Erro ao carregar exames');
        setExams([]);
      } finally {
        setLoading(false);
      }
    };

    fetchExams();
  }, []);

  const getStatusColor = (status) => {
    switch(status) {
      case 'disponível': return '#529ea3';
      case 'inscrito': return '#0f9d58';
      case 'não disponível': return '#ff6767';
      case 'encerrado': return '#333333';
      default: return '#5f6368';
    }
  };

  const getStatusBgColor = (status) => {
    switch(status) {
      case 'disponível': return '#e6f2f3';
      case 'inscrito': return '#e6f4ea';
      case 'não disponível': return '#ffe6e6';
      case 'encerrado': return '#f5f5f5';
      default: return '#f1f3f4';
    }
  };

  if (loading) {
    return (
      <div className="esp-exam-calendar-container">
        <div className="esp-calendar-header">
          <h2>Programação de Exames</h2>
          <div className="esp-calendar-period">Carregando...</div>
        </div>
        <div className="esp-calendar-list" style={{ textAlign: 'center', padding: '40px' }}>
          <div>Carregando exames...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="esp-exam-calendar-container">
        <div className="esp-calendar-header">
          <h2>Programação de Exames</h2>
          <div className="esp-calendar-period">Erro ao carregar</div>
        </div>
        <div className="esp-calendar-list" style={{ textAlign: 'center', padding: '40px', color: '#ff6767' }}>
          <div>Erro ao carregar exames: {error}</div>
          <button 
            onClick={() => window.location.reload()} 
            className="esp-btn-primary"
            style={{ marginTop: '20px' }}
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (exams.length === 0) {
    return (
      <div className="esp-exam-calendar-container">
        <div className="esp-calendar-header">
          <h2>Programação de Exames</h2>
          <div className="esp-calendar-period">Nenhum exame disponível</div>
        </div>
        <div className="esp-calendar-list" style={{ textAlign: 'center', padding: '40px', color: '#5f6368' }}>
          <div>Nenhum exame disponível no momento.</div>
        </div>
      </div>
    );
  }

  // Calculate date range for header
  const getDateRange = () => {
    if (exams.length === 0) return 'Nenhum exame';
    
    const dates = exams.map(exam => {
      const date = new Date();
      date.setDate(exam.day);
      // Note: This is a simplified approach - in production you'd want to use actual dates
      return date;
    });
    
    const sortedDates = dates.sort((a, b) => a - b);
    const firstDate = sortedDates[0];
    const lastDate = sortedDates[sortedDates.length - 1];
    
    const firstMonth = firstDate.toLocaleDateString('pt-BR', { month: 'short' }).toUpperCase();
    const firstYear = firstDate.getFullYear();
    const lastMonth = lastDate.toLocaleDateString('pt-BR', { month: 'short' }).toUpperCase();
    const lastYear = lastDate.getFullYear();
    
    return `${firstMonth}. ${firstYear} – ${lastMonth}. ${lastYear}`;
  };

  return (
    <div className="esp-exam-calendar-container">
      <div className="esp-calendar-header">
        <h2>Programação de Exames</h2>
        <div className="esp-calendar-period">{getDateRange()}</div>
      </div>
      <div className="esp-calendar-list">
        {exams.map((exam) => (
          <div 
            key={exam.id} 
            className={`esp-calendar-item ${exam.status === 'não disponível' || exam.status === 'encerrado' ? 'esp-disabled' : ''}`}
            onClick={() => exam.status !== 'não disponível' && exam.status !== 'encerrado' && onExamSelect(exam)}
          >
            <div className="esp-calendar-item-date">
              <div className="esp-date-number">{exam.day}</div>
              <div className="esp-date-info">
                <span className="esp-date-month">{exam.month}</span>
                <span className="esp-date-weekday">{exam.weekday}</span>
              </div>
            </div>
            <div className="esp-calendar-item-content">
              <div className="esp-exam-name">{exam.name}</div>
              <div 
                className="esp-exam-status"
                style={{ 
                  color: getStatusColor(exam.status),
                  backgroundColor: getStatusBgColor(exam.status)
                }}
              >
                ● {exam.status.toUpperCase()}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// --- Main Component: ExamSubscription ---

const stepComponents = [
  StepIntroduction, StepSecurityChallenge, StepGetCPF, StepGetBirthDate,
  StepReviewData, StepGetFatherName, StepReadyToStart, StepSchoolType,
  StepHighSchoolStatus, StepLanguageChoice, StepAccessibilityNeeds,
  StepAccessibilityResources, StepCEP, StepAddressInfo, StepDocumentInfo,
  StepBirthLocation, StepPersonalInfo, StepSocioeconomicIntro, StepNIS,
  StepEducationType, StepEncceja, StepInternetAccess, StepPhotoUpload,
  StepExamLocation, StepExamTypeChoice, StepContactInfo, StepFinalReview,
];

function ExamSubscription() {
  const [showCalendar, setShowCalendar] = useState(true);
  const [selectedExam, setSelectedExam] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  const [hasPreviousData, setHasPreviousData] = useState(false);

  useEffect(() => {
    const data = loadFromLocalStorage();
    setHasPreviousData(!!data);
  }, []);

  useEffect(() => {
    if (!showCalendar && currentStep >= stepComponents.length) {
      if (redirectCountdown > 0) {
        const timer = setTimeout(() => setRedirectCountdown(redirectCountdown - 1), 1000);
        return () => clearTimeout(timer);
      } else {
        handleBackToCalendar();
      }
    }
  }, [redirectCountdown, showCalendar, currentStep]);

  const handleExamSelect = (exam) => {
    setSelectedExam(exam);
    setShowCalendar(false);
    setCurrentStep(0);
    setFormData({});
  };

  const handleResume = () => {
    const savedData = loadFromLocalStorage();
    if (savedData) {
      setSelectedExam({ name: 'ENEM 2026 - 1º Dia' });
      setShowCalendar(false);
      setFormData(savedData);
      setCurrentStep(2); // Start from CPF
    }
  };

  const handleBackToCalendar = () => {
    setShowCalendar(true);
    setSelectedExam(null);
    setCurrentStep(0);
    setFormData({});
    setRedirectCountdown(5);
  };

  const handleNext = (data = {}) => {
    const newData = { ...formData, ...data };
    setFormData(newData);
    if (currentStep < stepComponents.length - 1) saveToLocalStorage(newData);
    setCurrentStep(prevStep => prevStep + 1);
  };
  
  const handlePrev = () => setCurrentStep(prevStep => Math.max(0, prevStep - 1));
  
  const CurrentStepComponent = stepComponents[currentStep];

  if (showCalendar) {
    return (
      <div className='page-container'>
        <div className='menu'><Sidebar /></div>
        <div className='content'><ExamCalendarList onExamSelect={handleExamSelect} /></div>
      </div>
    );
  }

  if (!CurrentStepComponent) {
    return (
      <div className='page-container'>
        <div className='menu'><Sidebar /></div>
        <div className='content'>
            <div className="esp-exam-subscription-container">
                <div className="esp-flow-complete-message">
                    <h2>🎉 Inscrição realizada com sucesso! 🎉</h2>
                    <p className="esp-redirect-message">Redirecionando em {redirectCountdown} segundos...</p>
                    <button onClick={handleBackToCalendar} className="esp-btn-primary">Voltar ao Calendário Agora</button>
                </div>
            </div>
        </div>
      </div>
    );
  }

  return (
    <div className='page-container'>
      <div className='menu'><Sidebar /></div>
      <div className='content'>
        <div className="esp-exam-subscription-container">
          <div className="esp-subscription-header">
            <button onClick={handleBackToCalendar} className="esp-back-to-calendar-btn">← Voltar</button>
            <h3>{selectedExam?.name}</h3>
          </div>
          <div className="esp-subscription-flow-carousel-wrapper">
              <div className="esp-subscription-flow-carousel" style={{ transform: `translateX(-${currentStep * 100}%)` }}>
                  {stepComponents.map((StepComponent, index) => {
                      // Check if this is the StepFinalReview component (last step)
                      const isFinalReview = index === stepComponents.length - 1;
                      
                      return (
                          <div key={index} className="esp-carousel-slide">
                              <div className="esp-flow-content-area">
                                  <div className="esp-flow-character-avatar"><div className="esp-avatar-placeholder">👩</div></div>
                                  {isFinalReview ? (
                                      <StepFinalReview 
                                          onNext={handleNext} 
                                          onPrev={handlePrev} 
                                          data={formData} 
                                          selectedExam={selectedExam}
                                      />
                                  ) : (
                                      <StepComponent 
                                          onNext={handleNext} 
                                          onPrev={handlePrev} 
                                          data={formData} 
                                          initialData={formData}
                                          onResume={handleResume}
                                          hasPreviousData={hasPreviousData}
                                      />
                                  )}
                              </div>
                          </div>
                      );
                  })}
              </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExamSubscription;